import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2, ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { RelatedBlogPosts } from './RelatedBlogPosts';
import { BlogPostData } from '../../types/blog';
import { NavBar } from '../NavBar';
interface BlogPostTemplateProps {
  content: BlogPostData;
}
export const BlogPostTemplate = memo(function BlogPostTemplate({
  content
}: BlogPostTemplateProps) {
  const {
    articleData,
    seo,
    featuredImage,
    alternateUrls,
    intro,
    tableOfContents,
    sections,
    protectionSection,
    whyAiWorksSection,
    faqSchema,
    relatedPosts,
    translations = {}
  } = content;
  const t = {
    tableOfContents: translations.tableOfContents || 'Table of Contents',
    continueLearning: translations.continueLearning || 'Continue Learning',
    readyToSeeTruth: translations.readyToSeeTruth || '💀 Ready to See the Truth?',
    readyToSeeTruthDesc: translations.readyToSeeTruthDesc || 'Gaslighting is subtle, dangerous, and exhausting, but our AI can make it crystal clear. Upload your chats and get your instant toxicity report.',
    scanChatsNow: translations.scanChatsNow || 'Scan Your Chats Now →',
    shareArticle: translations.shareArticle || 'Share this article',
    backToBlog: translations.backToBlog || 'Back to Blog',
    protectionSectionTitle: translations.protectionSectionTitle || '🛡 How to Protect Yourself',
    whyAiWorksTitle: translations.whyAiWorksTitle || '💀 Why Our AI Works'
  };
  const handleShare = async () => {
    try {
      await navigator.share({
        title: articleData.title,
        text: articleData.description,
        url: window.location.href
      });
    } catch (err) {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleData.title,
    description: articleData.description,
    image: featuredImage.url,
    datePublished: articleData.publishDate,
    author: {
      '@type': 'Organization',
      name: articleData.author
    },
    publisher: {
      '@type': 'Organization',
      name: 'ToxicDetector',
      logo: {
        '@type': 'ImageObject',
        url: 'https://toxicdetector.com/images/logo.png'
      }
    }
  };
  return <>
      <Helmet data-id="element-581">
        <title data-id="element-582">{articleData.title}</title>
        <meta name="description" content={articleData.description} data-id="element-583" />
        <meta name="keywords" content={seo.keywords} data-id="element-584" />
        <link rel="canonical" href={`https://toxicdetector.com/blog/${articleData.slug}`} data-id="element-585" />

        {alternateUrls?.map(alt => <link key={alt.lang} rel="alternate" hrefLang={alt.lang} href={alt.url} data-id="element-586" />)}

        <meta property="og:type" content="article" data-id="element-587" />
        <meta property="og:title" content={articleData.title} data-id="element-588" />
        <meta property="og:description" content={articleData.description} data-id="element-589" />
        <meta property="og:url" content={`https://toxicdetector.com/blog/${articleData.slug}`} data-id="element-590" />
        <meta property="og:image" content={seo.ogImage} data-id="element-591" />

        <meta name="twitter:card" content="summary_large_image" data-id="element-592" />
        <meta name="twitter:title" content={seo.twitterTitle || articleData.title} data-id="element-593" />
        <meta name="twitter:description" content={seo.twitterDescription || articleData.description} data-id="element-594" />
        <meta name="twitter:image" content={seo.twitterImage} data-id="element-595" />

        <script type="application/ld+json" data-id="element-596">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json" data-id="element-597">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="flex flex-col w-full min-h-screen bg-gradient-to-b from-stone-50 to-white" data-id="element-598">
        <NavBar data-id="element-599" />
        <header className="bg-white/90 border-b border-gray-200 sticky top-0 z-50 backdrop-blur-sm" data-id="element-600">
          <div className="max-w-3xl mx-auto px-6 py-4" data-id="element-601">
            <div className="flex items-center justify-between" data-id="element-602">
              <a href="/" className="text-2xl font-bold tracking-tight text-gray-900" data-id="element-603">
                🚩 ToxicDetector
              </a>
              <button onClick={handleShare} className="p-2.5 hover:bg-gray-50 rounded-lg transition-colors" aria-label="Share article" data-id="element-604">
                <Share2 size={20} className="text-gray-600" data-id="element-605" />
              </button>
            </div>
          </div>
        </header>

        <article className="max-w-3xl mx-auto w-full px-6 py-12" data-id="element-606">
          <a href="/blog" className="inline-flex items-center text-gray-600 text-sm mb-10 hover:text-gray-900 transition-colors group" data-id="element-607">
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-608" />
            {t.backToBlog}
          </a>

          <header className="mb-12" data-id="element-609">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight text-gray-900" data-id="element-610">
              {articleData.title}
            </h1>
            <div className="flex items-center text-gray-600 text-sm gap-3" data-id="element-611">
              <time dateTime={articleData.publishDate} data-id="element-612">
                {new Date(articleData.publishDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              </time>
              <span className="text-gray-300" data-id="element-613">•</span>
              <span data-id="element-614">{articleData.author}</span>
            </div>
          </header>

          <div className="mb-12" data-id="element-615">
            <img src={featuredImage.url} alt={featuredImage.alt} className="w-full rounded-2xl shadow-sm border border-gray-200" data-id="element-616" />
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 mb-12" data-id="element-617">
            <h2 className="font-bold text-lg mb-4 text-gray-900" data-id="element-618">
              {t.tableOfContents}
            </h2>
            <ul className="space-y-3 text-sm" data-id="element-619">
              {tableOfContents.map(item => <li key={item.id} data-id="element-620">
                  <a href={`#${item.id}`} className="text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2" data-id="element-621">
                    <span className="text-gray-400" data-id="element-622">→</span>
                    {item.title}
                  </a>
                </li>)}
              <li data-id="element-623">
                <a href="#faq" className="text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2" data-id="element-624">
                  <span className="text-gray-400" data-id="element-625">→</span>
                  Frequently Asked Questions
                </a>
              </li>
            </ul>
          </div>

          <div className="prose prose-lg max-w-none" data-id="element-626">
            {intro.map((paragraph, idx) => <p key={idx} className={`mb-8 leading-relaxed ${idx === 0 ? 'text-xl text-gray-700' : 'text-lg text-gray-600'}`} dangerouslySetInnerHTML={{
            __html: paragraph
          }} data-id="element-627" />)}

            {sections.map(section => <section key={section.id} id={section.id} className="mb-12" data-id="element-628">
                <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-629">
                  {section.title}
                </h2>
                {section.quote && <div className="bg-gray-50 border-l-4 border-purple-600 p-6 mb-6 rounded-r-xl" data-id="element-630">
                    <p className="italic text-gray-700 text-lg" data-id="element-631">
                      "{section.quote}"
                    </p>
                  </div>}
                {section.content.map((paragraph, idx) => <p key={idx} className="text-lg leading-relaxed text-gray-600 mb-4" dangerouslySetInnerHTML={{
              __html: paragraph
            }} data-id="element-632" />)}
              </section>)}

            {protectionSection && <section className="mb-12" data-id="element-633">
                <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-634">
                  {t.protectionSectionTitle}
                </h2>
                <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-635">
                  <ul className="space-y-3 text-lg text-gray-600" data-id="element-636">
                    {protectionSection.tips.map((tip, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-637">
                        <span className="text-purple-600 font-bold" data-id="element-638">•</span>
                        <span dangerouslySetInnerHTML={{
                    __html: tip
                  }} data-id="element-639" />
                      </li>)}
                  </ul>
                </div>
              </section>}

            {whyAiWorksSection && <section className="mb-12" data-id="element-640">
                <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-641">
                  {t.whyAiWorksTitle}
                </h2>
                <p className="mb-6 text-lg leading-relaxed text-gray-600" data-id="element-642">
                  {whyAiWorksSection.intro}
                </p>
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-id="element-643">
                  <ul className="space-y-3 text-lg text-gray-600" data-id="element-644">
                    {whyAiWorksSection.bullets.map((bullet, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-645">
                        <span className="text-purple-600 font-bold" data-id="element-646">•</span>
                        <span dangerouslySetInnerHTML={{
                    __html: bullet
                  }} data-id="element-647" />
                      </li>)}
                  </ul>
                </div>
                <p className="text-lg font-semibold text-gray-900" data-id="element-648">
                  {whyAiWorksSection.outro}
                </p>
                <div className="mt-6" data-id="element-649">
                  <Link to="/upload" className="inline-block bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors" data-id="element-650">
                    See how Toxic Detector analyzes patterns →
                  </Link>
                </div>
              </section>}

            <section id="faq" className="mb-12" data-id="element-651">
              <h2 className="text-3xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-652">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4" data-id="element-653">
                {faqSchema.mainEntity.map((faq, index) => <details key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden group" data-id="element-654">
                    <summary className="px-6 py-4 cursor-pointer font-semibold text-base text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-between" data-id="element-655">
                      <span data-id="element-656">{faq.name}</span>
                      <ChevronDown size={20} className="text-gray-400 group-open:rotate-180 transition-transform" data-id="element-657" />
                    </summary>
                    <div className="px-6 pb-4 text-gray-600 leading-relaxed text-sm" data-id="element-658">
                      {faq.acceptedAnswer.text}
                    </div>
                  </details>)}
              </div>
            </section>

            <section className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-12" data-id="element-659">
              <h3 className="text-xl font-bold mb-4 text-gray-900" data-id="element-660">
                {t.continueLearning}
              </h3>
              <div className="space-y-3" data-id="element-661">
                <a href="/blog/narcissism-and-gaslighting-explained" className="block p-4 bg-white rounded-lg hover:shadow-sm transition-shadow border border-gray-200" data-id="element-662">
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm" data-id="element-663">
                    Learn about narcissism & gaslighting →
                  </h4>
                  <p className="text-xs text-gray-600" data-id="element-664">
                    Understand the psychology behind manipulation tactics
                  </p>
                </a>
                <a href="/blog/is-your-ex-covert-narcissist" className="block p-4 bg-white rounded-lg hover:shadow-sm transition-shadow border border-gray-200" data-id="element-665">
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm" data-id="element-666">
                    Is your ex a covert narcissist? →
                  </h4>
                  <p className="text-xs text-gray-600" data-id="element-667">
                    Discover hidden signs of covert narcissistic behavior
                  </p>
                </a>
                <Link to="/signup" className="block p-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors" data-id="element-668">
                  <h4 className="font-semibold mb-1 text-sm" data-id="element-669">
                    Try the Toxic Detector for free →
                  </h4>
                  <p className="text-xs text-purple-100" data-id="element-670">
                    Upload your chats and get instant AI-powered analysis
                  </p>
                </Link>
              </div>
            </section>
          </div>

          <div className="bg-purple-600 rounded-2xl p-10 text-center text-white mt-16" data-id="element-671">
            <h2 className="text-3xl font-bold mb-4 tracking-tight" data-id="element-672">
              {t.readyToSeeTruth}
            </h2>
            <p className="text-purple-100 mb-8 text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-673">
              {t.readyToSeeTruthDesc}
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/20" data-id="element-674">
              <p className="font-bold text-xl" data-id="element-675">
                "🚩 83% toxic. Beware of manipulation!"
              </p>
            </div>
            <Link to="/signup" className="inline-block bg-white text-purple-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-50 transition-colors" data-id="element-676">
              {t.scanChatsNow}
            </Link>
          </div>

          <div style={{
          marginTop: '40px',
          paddingTop: '20px',
          borderTop: '1px solid #eee'
        }} data-id="element-677">
            <h3 data-id="element-678">Related Articles</h3>
            <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginTop: '10px'
          }} data-id="element-679">
              <a href="/blog/is-your-ex-a-covert-narcissist" data-id="element-680">
                Covert Narcissist Signs
              </a>
              <a href="/blog/passive-aggressive-texts-guide" data-id="element-681">
                Passive Aggressive Texts
              </a>
              <a href="/blog/narcissism-gaslighting-explained" data-id="element-682">
                Gaslighting Explained
              </a>
            </div>
          </div>

          <RelatedBlogPosts posts={relatedPosts} data-id="element-683" />

          <div className="mt-12 text-center" data-id="element-684">
            <button onClick={handleShare} className="inline-flex items-center text-gray-600 hover:text-gray-900 font-medium transition-colors text-sm" data-id="element-685">
              <Share2 size={18} className="mr-2" data-id="element-686" />
              {t.shareArticle}
            </button>
          </div>
        </article>

        <footer className="bg-white border-t border-gray-200 py-12 mt-16" data-id="element-687">
          <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-688">
            <p className="text-gray-500 text-sm" data-id="element-689">
              © {new Date().getFullYear()} ToxicDetector. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
});